import { hasOutstandingInvoiceCredit } from './invoiceCreditDiscrepancy'
/**
 * Match-action tone for the invoices table Match button + Match modal.
 *
 * Green  — full match within tolerance → Match & reconcile
 * Amber  — partial / outside tolerance → Link only (GR still needed)
 * Red    — no usable single match → Link only after user picks a PO
 *
 * Credit notes (Chunk C2) always use Apply to PO — never Match & reconcile.
 *
 * Matching-status badges stay separate; this is only the primary action cue.
 */

import {
  canEditInvoiceDetails,
  getInvoiceMatchingFailureReasonMeta,
} from './invoiceUploadHelpers'
import {
  CN_DOCUMENT_COPY,
  isCreditNoteDocument,
} from './creditNoteDocumentHelpers'
import {
  getNoOrderMatchGuidance,
  shouldShowNoOrderMatchGuidance,
} from './invoiceNoOrderGuidance'

export const MATCH_ACTION_TONE = {
  GREEN: 'green',
  AMBER: 'amber',
  RED: 'red',
}

export const MATCH_ACTION_PRIMARY = {
  MATCH_AND_RECONCILE: 'match_and_reconcile',
  LINK_ONLY: 'link_only',
  APPLY_TO_PO: 'apply_to_po',
}

/** Visual tokens for Match button border / surface by tone. */
export const MATCH_ACTION_TONE_STYLES = {
  green: {
    border: '#52c41a',
    background: '#f6ffed',
    color: '#135200',
    activeBorder: '#389e0d',
    activeBackground: '#d9f7be',
  },
  amber: {
    border: '#faad14',
    background: '#fffbe6',
    color: '#874d00',
    activeBorder: '#d48806',
    activeBackground: '#fff1b8',
  },
  red: {
    border: '#ff4d4f',
    background: '#fff2f0',
    color: '#a8071a',
    activeBorder: '#cf1322',
    activeBackground: '#ffccc7',
  },
}

const OUTSIDE_TOLERANCE_FAILURE = 'total_outside_tolerance'
const PO_ALREADY_FULLY_INVOICED_FAILURE = 'po_already_fully_invoiced'
const TERMINAL_LIFECYCLE = new Set(['reconciled', 'sentToXero'])

export function isInvoiceAlreadyAttached(invoice = {}) {
  return (invoice.status || '') === 'attachedToPO'
}

function hasRecommendedPo(invoice = {}) {
  if (
    Array.isArray(invoice.matchedPurchaseOrderNumbers) &&
    invoice.matchedPurchaseOrderNumbers.some(Boolean)
  ) {
    return true
  }
  if (String(invoice.matchSummary?.purchaseOrderNumber || '').trim()) {
    return true
  }
  if (
    Array.isArray(invoice.matchedOrderIds) &&
    invoice.matchedOrderIds.some(Boolean)
  ) {
    return true
  }
  return false
}

function isOneClickEligible(invoice = {}) {
  if (hasOutstandingInvoiceCredit(invoice)) return false
  // Explicit false wins — never offer Match & reconcile when the engine says
  // only ordered would fit (or any other non-one-click verdict).
  if (invoice.matchSummary?.oneClickEligible === false) return false
  if (invoice.matchSummary?.oneClickEligible === true) return true
  return invoice.matchingStatus === 'match_found'
}

function isOutsideToleranceReview(invoice = {}) {
  return (
    invoice.matchingStatus === 'needs_action' &&
    invoice.matchingFailureReason === OUTSIDE_TOLERANCE_FAILURE
  )
}

export function getInvoiceMatchActionToneStyles(tone = MATCH_ACTION_TONE.RED) {
  return (
    MATCH_ACTION_TONE_STYLES[tone] ||
    MATCH_ACTION_TONE_STYLES[MATCH_ACTION_TONE.RED]
  )
}

/**
 * Whether the Match CTA should appear for this invoice row.
 * Terminal lifecycle rows are done; Possible Duplicate is handled in a later
 * step as a gate before Match, but still gets a tone for when Match is shown.
 */
export function canShowMatchAction(invoice = {}) {
  const lifecycle = invoice.status || 'pending'
  if (TERMINAL_LIFECYCLE.has(lifecycle)) return false
  if (invoice.isArchived) return false
  return true
}

/**
 * Shared Match-action view model from saved matching fields.
 *
 * @returns {{
 *   tone: 'green'|'amber'|'red',
 *   reason: string,
 *   label: string,
 *   guidance: string,
 *   primaryAction: 'match_and_reconcile'|'link_only',
 *   hasRecommendedPo: boolean,
 *   styles: object,
 * }}
 */
export function getInvoiceMatchActionMeta(invoice = {}) {
  if (isCreditNoteDocument(invoice)) {
    const alreadyApplied = isInvoiceAlreadyAttached(invoice)
    return {
      tone: MATCH_ACTION_TONE.AMBER,
      reason: alreadyApplied ? 'credit_note_applied' : 'credit_note_manual',
      label: alreadyApplied ? 'Applied' : CN_DOCUMENT_COPY.matchingChipLabel,
      guidance: alreadyApplied
        ? 'This credit note is applied to a PO. Remaining balance nets invoices + credits — it does not reconcile on its own.'
        : CN_DOCUMENT_COPY.applyActionTooltip,
      primaryAction: MATCH_ACTION_PRIMARY.APPLY_TO_PO,
      hasRecommendedPo: hasRecommendedPo(invoice),
      styles: getInvoiceMatchActionToneStyles(MATCH_ACTION_TONE.AMBER),
    }
  }

  const status = invoice.matchingStatus || 'unprocessed'
  const recommended = hasRecommendedPo(invoice)

  let tone = MATCH_ACTION_TONE.RED
  let reason = status
  let label = 'No match yet'
  let guidance =
    'We could not confidently match this invoice. Choose a purchase order to link it.'

  if (status === 'possible_duplicate') {
    tone = MATCH_ACTION_TONE.RED
    reason = 'possible_duplicate'
    label = 'Possible duplicate'
    guidance =
      'Review the possible duplicate before matching. Archive the copy you don’t want, or mark this as not a duplicate.'
  } else if (hasOutstandingInvoiceCredit(invoice)) {
    tone = MATCH_ACTION_TONE.AMBER
    reason = 'awaiting_credit'
    label = 'Awaiting credit'
    guidance =
      'Credit has been requested. Review the actual credit notes and outstanding difference before reconciling.'
  } else if (isOneClickEligible(invoice)) {
    tone = MATCH_ACTION_TONE.GREEN
    reason = 'full_match_in_tolerance'
    label = 'Full match'
    const receivedBasis = invoice.matchSummary?.orderGrossBasis === 'receipted'
    const alreadyAttached = isInvoiceAlreadyAttached(invoice)
    if (alreadyAttached) {
      guidance =
        'Already linked and totals align. Open Match and choose Match & reconcile to Full accept on Goods Receipting and mark Status Reconciled — Link only will not finish it.'
    } else {
      guidance = receivedBasis
        ? 'This invoice matches the received order total within tolerance. Match & reconcile attaches it on Goods Receipting and Full accepts the order — same as signing it off there. Link only attaches without reconciling.'
        : 'This invoice matches the recommended order within tolerance. Match & reconcile attaches it on Goods Receipting and Full accepts the order — same as signing it off there. Link only attaches without reconciling.'
    }
  } else if (status === 'partial_match') {
    tone = MATCH_ACTION_TONE.AMBER
    reason = 'partial_match'
    label = 'Partial match'
    guidance =
      'This invoice matches the PO but does not fully close the balance. Link it now, then finish reconciliation on the Goods Receipting page when remaining invoices arrive.'
  } else if (isOutsideToleranceReview(invoice)) {
    tone = MATCH_ACTION_TONE.AMBER
    reason = 'outside_tolerance'
    label = 'Outside tolerance'
    guidance =
      'Totals differ beyond your match tolerance. If the supplier sent a credit for a short or damaged delivery, upload a Credit Note and apply it to this PO — then the balance can net to zero. Otherwise link the invoice and finish on Goods Receipting.'
  } else if (
    invoice.matchingStatus === 'needs_action' &&
    invoice.matchingFailureReason === PO_ALREADY_FULLY_INVOICED_FAILURE
  ) {
    tone = MATCH_ACTION_TONE.AMBER
    reason = PO_ALREADY_FULLY_INVOICED_FAILURE
    label = 'PO already fully invoiced'
    guidance =
      String(invoice.matchingReasonDetail || '').trim() ||
      'This PO is already fully invoiced. Raising match tolerance will not help — review linked invoices, or unlink if this file belongs elsewhere.'
  } else if (status === 'multiple_matches') {
    tone = MATCH_ACTION_TONE.RED
    reason = 'multiple_matches'
    label = 'Multiple matches'
    guidance = isInvoiceAlreadyAttached(invoice)
      ? 'More than one order shares this PO. Matching will prefer the order you linked — open Match to refresh, then Match & reconcile when totals align. Or pick a different order first.'
      : 'More than one order shares this PO. Choose the correct order below, then Match & reconcile (or Link only, then Finish reconcile on Goods Receipting).'
  } else if (status === 'no_match') {
    tone = MATCH_ACTION_TONE.RED
    reason = 'no_match'
    label = 'No match'
    guidance =
      'No matching OMS order was found. If ordered outside OMS, add a manual order on Orders first; if ordered in OMS, fix Goods receipting — then come back to Match.'
  } else if (status === 'needs_action') {
    tone = MATCH_ACTION_TONE.RED
    reason = invoice.matchingFailureReason || 'needs_action'
    const failureMeta = getInvoiceMatchingFailureReasonMeta(reason)
    label =
      reason === 'missing_po' ||
      reason === 'missing_supplier' ||
      reason === 'non_oms_supplier'
        ? failureMeta.label || 'Review needed'
        : 'Review needed'
    guidance =
      String(invoice.matchingReasonDetail || '').trim() ||
      failureMeta.guidance ||
      'We could not confidently match this invoice. Choose a purchase order to continue.'
  } else if (status === 'unprocessed') {
    tone = MATCH_ACTION_TONE.RED
    reason = 'unprocessed'
    label = 'Unprocessed'
    guidance = isInvoiceAlreadyAttached(invoice)
      ? 'Matching has not refreshed since this invoice was linked. Open Match to rematch, then Match & reconcile when totals align.'
      : 'Matching has not finished for this invoice yet. You can still choose a purchase order to link it.'
  }

  // Amber/red with no PO still use link_only; green always match_and_reconcile.
  const primaryAction =
    tone === MATCH_ACTION_TONE.GREEN
      ? MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE
      : MATCH_ACTION_PRIMARY.LINK_ONLY

  return {
    tone,
    reason,
    label,
    guidance,
    primaryAction,
    hasRecommendedPo: recommended,
    styles: getInvoiceMatchActionToneStyles(tone),
  }
}

/** Convenience: just the tone key. */
export function getInvoiceMatchActionTone(invoice = {}) {
  return getInvoiceMatchActionMeta(invoice).tone
}

/**
 * Manual Link / Apply requires an identified OMS order id — raw PO text is
 * not unique across suppliers (same string can exist for Brakes and Grahams).
 */
export function hasIdentifiedOrderForInvoiceLink({
  linkedOrderId = null,
  selectedIsRecommended = false,
  recommendedOrderId = null,
} = {}) {
  if (String(linkedOrderId || '').trim()) return true
  if (selectedIsRecommended && String(recommendedOrderId || '').trim()) {
    return true
  }
  return false
}

/**
 * Whether Match modal may offer Match & reconcile for the current selection.
 * Green recommended path, or a deliberate order pick when PO collides
 * (multiple_matches / soft-linked unprocessed) — never silent auto-pick.
 */
export function canOfferMatchAndReconcileForSelection(
  invoice = {},
  { selectedIsRecommended = false, linkedOrderId = null } = {},
) {
  if (isCreditNoteDocument(invoice) || hasOutstandingInvoiceCredit(invoice))
    return false

  const meta = getInvoiceMatchActionMeta(invoice)
  if (
    meta.primaryAction === MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE &&
    selectedIsRecommended
  ) {
    return true
  }

  const hasOrderPick = Boolean(String(linkedOrderId || '').trim())
  if (!hasOrderPick) return false

  const status = invoice.matchingStatus || 'unprocessed'
  if (status === 'multiple_matches') {
    return true
  }

  if (
    isInvoiceAlreadyAttached(invoice) &&
    (status === 'unprocessed' || status === 'needs_action') &&
    meta.reason !== 'outside_tolerance'
  ) {
    return true
  }

  return false
}

/**
 * Match-modal footer + copy for the happy path (Step 3).
 * Green / one-click → Match & reconcile is primary; Link only stays secondary
 * with explicit “attach ≠ reconciled” wording. Already-attached green rows
 * point straight at finishing reconcile.
 *
 * @param {object} invoice
 * @param {{
 *   canMatchAndReconcile?: boolean,
 *   selectedIsRecommended?: boolean,
 *   deliberateOrderPick?: boolean,
 * }} [options]
 */
export function getMatchModalActionPresentation(
  invoice = {},
  {
    canMatchAndReconcile = false,
    selectedIsRecommended = false,
    deliberateOrderPick = false,
  } = {},
) {
  const meta = getInvoiceMatchActionMeta(invoice)
  const alreadyAttached = isInvoiceAlreadyAttached(invoice)

  if (meta.primaryAction === MATCH_ACTION_PRIMARY.APPLY_TO_PO) {
    const cnPoLookupFailure = shouldShowNoOrderMatchGuidance(invoice)
    const cnNoOrder = cnPoLookupFailure
      ? getNoOrderMatchGuidance(invoice)
      : null
    return {
      alreadyAttached,
      primaryLabel: alreadyAttached
        ? 'Keep applied'
        : CN_DOCUMENT_COPY.applyActionLabel,
      primaryIsMatchAndReconcile: false,
      primaryIsApplyToPo: true,
      showLinkOnlySecondary: false,
      linkOnlyLabel: '',
      bannerGuidance: alreadyAttached
        ? meta.guidance
        : cnNoOrder
        ? `${cnNoOrder.title}. Search below and pick the correct OMS PO to Apply.`
        : CN_DOCUMENT_COPY.matchingGuidance,
      showHappyPathNote: true,
      happyPathNote: cnNoOrder
        ? 'Credit notes often cite an invoice number (“With Reference To”), not your OMS PO. Pick the right purchase order below, then Apply.'
        : CN_DOCUMENT_COPY.applyActionTooltip,
      showGoodsReceiptNote: false,
      goodsReceiptNote: '',
      tableButtonLabel: alreadyAttached
        ? 'Applied'
        : CN_DOCUMENT_COPY.applyActionLabel,
      tone: meta.tone,
      styles: meta.styles,
    }
  }

  if (canMatchAndReconcile) {
    const deliberateGuidance =
      'You chose a specific order among several that share this PO. Match & reconcile attaches that order and Full accepts when totals align — we will not auto-pick a colliding PO.'

    const guidance = deliberateOrderPick
      ? deliberateGuidance
      : alreadyAttached
      ? 'This invoice is already linked to the recommended PO. Match & reconcile finishes the job — Full accept on Goods Receipting and mark Status Reconciled. Link only leaves it attached without reconciling.'
      : meta.guidance

    return {
      alreadyAttached,
      primaryLabel: 'Match & reconcile',
      primaryIsMatchAndReconcile: true,
      showLinkOnlySecondary: true,
      linkOnlyLabel: alreadyAttached ? 'Stay linked only' : 'Link only',
      bannerGuidance: guidance,
      showHappyPathNote: !deliberateOrderPick,
      happyPathNote:
        'Match & reconcile closes the order (same as Full accept on Goods Receipting). Link only attaches — it does not mark the invoice Reconciled.',
      showGoodsReceiptNote: false,
      goodsReceiptNote: '',
      tableButtonLabel: alreadyAttached ? 'Reconcile' : 'Match',
    }
  }

  // Green engine verdict but user picked a different PO — link only.
  const choseAlternatePo =
    meta.tone === MATCH_ACTION_TONE.GREEN && !selectedIsRecommended

  return {
    alreadyAttached,
    primaryLabel: 'Link this invoice',
    primaryIsMatchAndReconcile: false,
    showLinkOnlySecondary: false,
    linkOnlyLabel: 'Link only',
    bannerGuidance: choseAlternatePo
      ? 'You chose a different purchase order than the recommended match. Linking will attach the invoice for reference — it will not auto-reconcile.'
      : meta.guidance,
    showHappyPathNote: false,
    happyPathNote: '',
    showGoodsReceiptNote: true,
    goodsReceiptNote: alreadyAttached
      ? 'This invoice is already attached. Linking again keeps it attached without reconciling — finish with Match & reconcile when Matching shows a full match, or Full accept on Goods Receipting.'
      : 'Linking attaches the invoice but does not fully reconcile the order. When you are ready to close it, continue on Goods Receipting (or use Match & reconcile when totals align).',
    tableButtonLabel: 'Match',
  }
}

/**
 * Failure reasons where missing / wrong invoice fields block a good Match.
 * Chefs should Fix details (Edit modal) then return to Match — one path.
 */
export const FIX_DETAILS_FROM_MATCH_REASONS = new Set([
  'missing_invoice_number',
  'missing_total',
  'missing_supplier',
  'missing_po',
  'non_oms_supplier',
  'looks_like_delivery_note',
])

export const FIX_DETAILS_FROM_MATCH_BUTTON_LABEL = 'Fix details'

/**
 * Where to fix details when Match is opened from a screen that cannot edit the
 * invoice inline (Goods receipting). Invoices & Credit Notes owns Edit.
 */
export const FIX_DETAILS_ELSEWHERE_HINT =
  'Open this invoice on Invoices & Credit Notes and use Edit, then come back to Match.'

function invoiceMissingCoreMatchFields(invoice = {}) {
  if (!String(invoice.invoiceNumber || '').trim()) return true
  if (invoice.invoiceTotal == null || invoice.invoiceTotal === '') return true
  return false
}

/**
 * Whether Match should offer a deep-link into Edit invoice details.
 */
export function shouldOfferFixDetailsFromMatch(invoice = {}) {
  if (!canEditInvoiceDetails(invoice)) return false

  const reason = String(invoice.matchingFailureReason || '').trim()
  if (FIX_DETAILS_FROM_MATCH_REASONS.has(reason)) return true

  const status = invoice.matchingStatus || 'unprocessed'
  if (status === 'needs_action' && invoiceMissingCoreMatchFields(invoice)) {
    return true
  }

  return false
}

/**
 * Match-modal callout for Review needed → Fix details → Match.
 *
 * `canFixDetailsHere` is false when the host screen has no Edit invoice modal
 * (Goods receipting): keep the callout, drop the dead button, point to Invoices.
 *
 * @returns {{
 *   offer: boolean,
 *   showButton: boolean,
 *   buttonLabel: string,
 *   guidance: string,
 *   elsewhereHint: string,
 *   reason: string,
 * }}
 */
export function getFixDetailsFromMatchMeta(
  invoice = {},
  { canFixDetailsHere = true } = {},
) {
  const offer = shouldOfferFixDetailsFromMatch(invoice)
  const reason = String(invoice.matchingFailureReason || '').trim()

  let guidance =
    'Correct the invoice details (number, total, supplier, or type), then return to Match.'

  if (reason === 'missing_invoice_number') {
    guidance = 'Add the invoice number, then return to Match.'
  } else if (reason === 'missing_total') {
    guidance = 'Add the invoice total (including VAT), then return to Match.'
  } else if (reason === 'missing_supplier' || reason === 'non_oms_supplier') {
    guidance =
      'Link the correct OMS supplier (or keep Non-OMS), then return to Match. We won’t silently pick a different supplier — you can still match by PO.'
  } else if (reason === 'missing_po') {
    guidance = canFixDetailsHere
      ? 'This invoice has no Order No / PO. Add the OMS PO via Fix details, or choose the order below.'
      : 'This invoice has no Order No / PO. Add the OMS PO on the invoice, or choose the order below.'
  } else if (reason === 'looks_like_delivery_note') {
    guidance =
      'Confirm this is an invoice or credit note and add a total, then return to Match.'
  } else if (invoiceMissingCoreMatchFields(invoice)) {
    guidance =
      'This invoice is missing details needed for matching. Fix them, then return to Match.'
  }

  return {
    offer,
    showButton: offer && canFixDetailsHere,
    buttonLabel: FIX_DETAILS_FROM_MATCH_BUTTON_LABEL,
    guidance,
    elsewhereHint: canFixDetailsHere ? '' : FIX_DETAILS_ELSEWHERE_HINT,
    reason,
  }
}
